<template>
  <Transition name="fade">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
    >
      <!-- Overlay -->
      <div
        class="absolute inset-0 bg-slate-950/70 backdrop-blur-xs"
        @click="$emit('cancel')"
      ></div>

      <!-- Modal Card -->
      <div
        class="relative bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-2xl shadow-2xl p-6 max-w-md w-full mx-auto overflow-hidden animate-in fade-in zoom-in-95 duration-200"
      >
        <!-- Top accent -->
        <div
          class="absolute top-0 left-0 right-0 h-1"
          :class="isDestructive ? 'bg-gradient-to-r from-rose-500 to-red-600' : 'bg-gradient-to-r from-indigo-500 to-blue-600'"
        ></div>

        <!-- Title -->
        <h2 class="text-lg font-bold text-slate-900 dark:text-white mb-2 tracking-tight">
          {{ title }}
        </h2>

        <!-- Custom slot (like notices or warnings) -->
        <slot name="body"></slot>

        <!-- Description -->
        <p class="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
          {{ description }}
        </p>

        <!-- Names List (if provided) -->
        <div
          v-if="names && names.length > 0"
          class="mb-5 bg-slate-50 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] rounded-xl p-3.5"
        >
          <p class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
            {{ itemLabel }}:
          </p>
          <ul class="text-xs text-slate-600 dark:text-slate-300 space-y-1.5 font-medium">
            <li
              v-for="(name, index) in displayedNames"
              :key="index"
              class="flex items-center gap-2"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500"></span>
              <span class="truncate font-semibold">{{ name }}</span>
            </li>
          </ul>
          <p
            v-if="remainingCount > 0"
            class="text-[11px] text-slate-500 dark:text-slate-400 mt-2 font-medium"
          >
            ... and {{ remainingCount }} more
          </p>
        </div>

        <!-- Buttons -->
        <div class="flex gap-2.5 justify-end pt-2 border-t border-slate-100 dark:border-[#18243A]">
          <button
            @click="$emit('cancel')"
            :disabled="isLoading"
            class="px-4 py-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-[#18243A] hover:bg-slate-200 dark:hover:bg-[#263449] rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            @click="$emit('confirm')"
            :disabled="isLoading"
            :class="[
              'px-4 py-2 text-xs sm:text-sm font-semibold text-white rounded-xl shadow-xs transition-all disabled:opacity-50 disabled:cursor-not-allowed',
              isDestructive
                ? 'bg-rose-600 hover:bg-rose-700'
                : 'bg-indigo-600 hover:bg-indigo-700',
            ]"
          >
            <span v-if="isLoading" class="inline-flex items-center gap-1.5">
              <AppLoader variant="button" class="w-3.5 h-3.5 text-white" />
              <span>Processing...</span>
            </span>
            <span v-else>{{ buttonText }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
  import { computed } from "vue";

  const props = defineProps({
    show: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "Confirm Action",
    },
    description: {
      type: String,
      default: "Are you sure?",
    },
    names: {
      type: Array,
      default: () => [],
    },
    itemLabel: {
      type: String,
      default: "Items",
    },
    buttonText: {
      type: String,
      default: "Confirm",
    },
    isDestructive: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  const displayedNames = computed(() => {
    return props.names?.slice(0, 3) || [];
  });

  const remainingCount = computed(() => {
    return Math.max(0, (props.names?.length || 0) - 3);
  });

  defineEmits(["confirm", "cancel"]);
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
